import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { RequestCategory } from '@kfolks/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { type RequestTypeDTO, requestCenterApi } from '@/services/request-center-api'

const CATEGORY_LABEL: Record<RequestCategory, string> = {
	[RequestCategory.EMPLOYEES_LIFECYCLE]: 'Employees Lifecycle',
	[RequestCategory.ACCESS_IDENTITY]: 'Access & Identity',
	[RequestCategory.CONTRACTORS_PARTNERS]: 'Contractors & Collaborators',
	[RequestCategory.PROJECTS]: 'Projects',
	[RequestCategory.INCIDENTS]: 'Incidents',
}

const CATEGORY_ORDER: RequestCategory[] = [
	RequestCategory.EMPLOYEES_LIFECYCLE,
	RequestCategory.ACCESS_IDENTITY,
	RequestCategory.CONTRACTORS_PARTNERS,
	RequestCategory.PROJECTS,
	RequestCategory.INCIDENTS,
]

/**
 * Catalog of request types the caller can submit. Backend already filters by
 * visibility — we just present them grouped by category with a free-text
 * filter on top. Empty after filter / empty for visibility both render an
 * empty state (different copy).
 */
export function CatalogPage() {
	const [query, setQuery] = useState('')
	const { data, isLoading, error } = useQuery({
		queryKey: ['request-types', { catalog: true }],
		queryFn: () => requestCenterApi.listRequestTypes({ limit: 100 }),
	})

	const grouped = useMemo(() => groupByCategory(filterTypes(data ?? [], query)), [data, query])

	if (isLoading) return <CatalogSkeleton />
	if (error) return <CatalogErrorState message={error instanceof Error ? error.message : 'Catalog failed to load'} />
	const empty = (data?.length ?? 0) === 0
	const emptyAfterFilter = !empty && grouped.every((g) => g.types.length === 0)

	return (
		<div className="space-y-6">
			<CatalogHeader />
			<SearchBox value={query} onChange={setQuery} />
			<CatalogBody empty={empty} emptyAfterFilter={emptyAfterFilter} grouped={grouped} query={query} />
		</div>
	)
}

function CatalogHeader() {
	return (
		<header className="space-y-2">
			<h1 className="text-2xl font-semibold tracking-tight">Request Catalog</h1>
			<p className="text-sm text-muted-foreground">Choose the request you'd like to submit.</p>
		</header>
	)
}

function SearchBox({ value, onChange }: { value: string; onChange: (next: string) => void }) {
	return (
		<Input
			type="search"
			value={value}
			onChange={(e) => onChange(e.target.value)}
			placeholder="Search request types…"
			aria-label="Search request types"
			className="max-w-sm"
		/>
	)
}

function CatalogBody({
	empty,
	emptyAfterFilter,
	grouped,
	query,
}: {
	empty: boolean
	emptyAfterFilter: boolean
	grouped: { category: RequestCategory; types: RequestTypeDTO[] }[]
	query: string
}) {
	if (empty) {
		return (
			<EmptyState
				title="No request types available to you yet"
				description="If you think this is wrong, ask an admin about your role and group memberships."
			/>
		)
	}
	if (emptyAfterFilter) {
		return <EmptyState title="Nothing matches your search" description={`No request types match "${query}".`} />
	}
	return (
		<>
			{grouped.map((g) =>
				g.types.length === 0 ? null : (
					<section key={g.category} aria-labelledby={`cat-${g.category}`} className="space-y-3">
						<h2 id={`cat-${g.category}`} className="text-lg font-medium">
							{CATEGORY_LABEL[g.category]}
						</h2>
						<div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
							{g.types.map((t) => (
								<RequestTypeCard key={t.id} type={t} />
							))}
						</div>
					</section>
				),
			)}
		</>
	)
}

function RequestTypeCard({ type }: { type: RequestTypeDTO }) {
	return (
		<Link to={`/requests-center/new/${type.slug}`} className="block focus:outline-none">
			<Card className="h-full transition-colors hover:border-primary/50 focus-visible:border-primary">
				<CardHeader>
					<CardTitle className="text-base">{type.name}</CardTitle>
				</CardHeader>
				{type.description ? (
					<CardContent className="text-sm text-muted-foreground">{type.description}</CardContent>
				) : null}
			</Card>
		</Link>
	)
}

function CatalogSkeleton() {
	return (
		<div className="space-y-6">
			<div className="h-7 w-48 animate-pulse rounded bg-muted" />
			<div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
				{[0, 1, 2].map((i) => (
					<div key={i} className="h-28 animate-pulse rounded-lg border bg-muted/40" />
				))}
			</div>
		</div>
	)
}

function CatalogErrorState({ message }: { message: string }) {
	return (
		<div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
			{message}
		</div>
	)
}

function EmptyState({ title, description }: { title: string; description: string }) {
	return (
		<div className="rounded-md border border-dashed p-8 text-center">
			<p className="text-base font-medium">{title}</p>
			<p className="mt-1 text-sm text-muted-foreground">{description}</p>
		</div>
	)
}

function filterTypes(types: RequestTypeDTO[], query: string): RequestTypeDTO[] {
	const q = query.trim().toLowerCase()
	if (q === '') return types
	return types.filter(
		(t) =>
			t.name.toLowerCase().includes(q) ||
			t.slug.toLowerCase().includes(q) ||
			(t.description ?? '').toLowerCase().includes(q),
	)
}

function groupByCategory(types: RequestTypeDTO[]): { category: RequestCategory; types: RequestTypeDTO[] }[] {
	const buckets: Record<RequestCategory, RequestTypeDTO[]> = {
		[RequestCategory.EMPLOYEES_LIFECYCLE]: [],
		[RequestCategory.ACCESS_IDENTITY]: [],
		[RequestCategory.CONTRACTORS_PARTNERS]: [],
		[RequestCategory.PROJECTS]: [],
		[RequestCategory.INCIDENTS]: [],
	}
	for (const t of types) buckets[t.category]?.push(t)
	return CATEGORY_ORDER.map((c) => ({ category: c, types: buckets[c] }))
}
